/* eslint-disable max-lines-per-function */
import { api } from "jiffy-api";
import { sendDataLayer, track } from "jiffy-analytics";
import { trackCheckoutStepCompleted } from "common/utils/rudderstack/tracker";
import paymentOverlay from "components/checkout_v2/payment-overlay/payment-overlay";
import { getBusinessLinePath } from "components/checkout_v2/checkout-config";
import { logCheckoutData, getDeliveryDates } from "./threads-helper";

const falseErrors = ["detected popup close", "component closed", "cancelled", "window is closed"];
const isErrorMessage = errorMsg => {
  if (typeof errorMsg === "string" || errorMsg instanceof String) {
    return falseErrors.every(str => !errorMsg.toLowerCase().includes(str));
  }

  return false;
};

const handlePaypalError = error => {
  paymentOverlay.hide();
  const message = error.message ? error.message.substr(0, 300) : error;

  if (!isErrorMessage(message)) {
    return;
  }

  if (window.Honeybadger) {
    const errStack = error.stack ? error.stack : "handlePaypalError JS";
    const errName = error.name ? error.name : "PaypalError";

    window.Honeybadger?.notify("[JS] Error on PayPal order", {
      params: {
        error: {
          message,
          name: errName,
          stack: errStack,
          error
        }
      }
    });
  }
};

const handleErrorResponse = (response, errorSource, customMsg = null) => {
  const message = customMsg || response?.errors?.[0] || "Failed to connect to server";
  handlePaypalError({ message, response, name: errorSource });
};

window.jiffyThreads = window.jiffyThreads || {};
window.jiffyThreads.payments = window.jiffyThreads.payments || {};
window.jiffyThreads.payments.loadPaypalButtons = async (mountElement, getContext) => {
  const { pageSource, userType, paypalOrderCreateData, height, orderNumber, countryCode } =
    getContext();

  const buttonAttrsPayPal = {
    fundingSource: window.paypal.FUNDING.PAYPAL,
    style: {
      size: "responsive",
      color: "blue",
      height: height || 44,
      label: "checkout",
      layout: "vertical"
    }
  };

  const button = window.paypal.Buttons({
    ...buttonAttrsPayPal,
    onClick() {
      paymentOverlay.show();
      logCheckoutData(orderNumber, { page: pageSource }, "paypal.click_checkout_button");
    },
    onCancel(data) {
      paymentOverlay.hide();
      logCheckoutData(orderNumber, data, "paypal.cancel");
    },
    async createOrder(data) {
      /* eslint-disable-next-line */
      if (!data || !data.paymentSource) return false;

      await getDeliveryDates(orderNumber);
      sendDataLayer({
        genericEvent: true,
        eventCategory: `Page: ${pageSource}`,
        event: "click: checkout: PayPal",
        eventLabel: userType
      });

      if (window.mixpanel) {
        track("checkout_started", {
          which_checkout: "paypal"
        });
      }
      trackCheckoutStepCompleted("order_checkout", "paypal_express_checkout_button_click");

      try {
        const payload = {
          ...paypalOrderCreateData,
          order_number: orderNumber
        };
        const response = await api.jiffyshirts.orders.createPaypalOrder(
          { order_number: orderNumber },
          payload
        );

        return response.paypal_order_id;
      } catch (error) {
        logCheckoutData(orderNumber, error.toString(), "paypal.create_order_error");
        throw new Error("Error on creating PayPal order");
      }
    },
    async onApprove(data) {
      logCheckoutData(orderNumber, data, "paypal.approve");

      const params = {
        checkout_no_confirmation: true,
        paypal_order_id: data.orderID,
        payment_method: "PayPal",
        payment_method_subtype: "paypal",
        payment_source: data.paymentSource,
        jiffy_classics_order: true,
        order_number: orderNumber,
        business_line_path: getBusinessLinePath()
      };

      await getDeliveryDates(orderNumber);

      await api.jiffyshirts.checkout
        .update({ state: "payment" }, params)
        .then(response => {
          const error = response.error || response.errors?.[0];

          if (error) {
            handleErrorResponse(response, "paypal.checkout_error_1", error);
          } else if (response.redirect_to) {
            window.location.href = response.redirect_to;
          }
        })
        .catch(error => {
          const errorMessage = (() => {
            try {
              const errorResponse = JSON.parse(error.message);
              return errorResponse.errors[0];
            } catch (e) {
              return null;
            }
          })();
          if (errorMessage) {
            handleErrorResponse(error, "paypal.checkout_error_1", errorMessage);
          } else {
            handleErrorResponse(error, "paypal.checkout_error_2");
          }
        });
    },
    onError: handlePaypalError,
    async onShippingAddressChange(data, actions) {
      const { shippingAddress } = data;
      if (shippingAddress.countryCode !== countryCode) {
        return actions.reject(data.errors.COUNTRY_ERROR);
      }

      const payload = {
        city: shippingAddress.city,
        zipcode: shippingAddress.postalCode,
        paypal_order_id: data.orderID,
        jiffy_classics_order: true
      };

      return new Promise((resolve, reject) => {
        try {
          api.productCollections
            .updateExpressCheckoutAddress({ order_number: orderNumber }, payload)
            .then(() => {
              resolve();
            });
        } catch (error) {
          reject(error);
        }
      });
    }
  });

  if (button.isEligible()) {
    button.render(mountElement);
  }
};
